/**
 * ClockHUD — Reloj del paciente visible arriba-centro.
 *
 * Refleja state.simulation.patientTime en mm:ss y muestra cuánto falta para
 * el próximo milestone de deterioro. Cambia de color (verde → naranja → rojo
 * pulsante) a medida que se acerca el evento, para que el alumno "sienta"
 * el tiempo corriendo.
 *
 * Sin dependencias con el resto de la UI: inyecta su propio estilo y se
 * auto-oculta si no hay trayectoria activa.
 */

import { DeteriorationEngine } from '../medical/DeteriorationEngine';

export class ClockHUD {
  private container: HTMLElement | null = null;
  private timeEl: HTMLElement | null = null;
  private subEl: HTMLElement | null = null;
  private fillEl: HTMLElement | null = null;

  constructor(private engine: DeteriorationEngine) {
    this.injectStyles();
    this.render();
  }

  private render(): void {
    if (document.getElementById('cgt-clock')) {
      this.container = document.getElementById('cgt-clock');
    } else {
      const clock = document.createElement('div');
      clock.id = 'cgt-clock';
      clock.innerHTML = `
        <div class="cgt-clock-label">TIEMPO DEL PACIENTE</div>
        <div class="cgt-clock-time" id="cgt-clock-time">00:00</div>
        <div class="cgt-clock-sub" id="cgt-clock-sub">—</div>
        <div class="cgt-clock-track"><div class="cgt-clock-fill" id="cgt-clock-fill"></div></div>
      `;
      document.body.appendChild(clock);
      this.container = clock;
    }
    this.timeEl = document.getElementById('cgt-clock-time');
    this.subEl = document.getElementById('cgt-clock-sub');
    this.fillEl = document.getElementById('cgt-clock-fill');
  }

  /**
   * Llamar cada frame desde el loop principal. Barato: solo lee state del
   * engine y actualiza innerText/classList. No layout thrashing.
   */
  update(): void {
    if (!this.container || !this.timeEl || !this.subEl || !this.fillEl) return;

    const s = this.engine.getClockState();
    if (!s) {
      this.container.style.display = 'none';
      return;
    }
    this.container.style.display = 'block';

    const totalSec = Math.max(0, s.minutes * 60);
    const mm = Math.floor(totalSec / 60).toString().padStart(2, '0');
    const ss = Math.floor(totalSec % 60).toString().padStart(2, '0');
    this.timeEl.textContent = `${mm}:${ss}`;

    if (s.nextMilestoneMin === null) {
      this.subEl.textContent = `Todos los eventos (${s.firedCount}/${s.totalMilestones}) transcurridos`;
      this.fillEl.style.width = '100%';
      this.fillEl.style.background = '#6e6e73';
      this.container.classList.remove('warn', 'danger');
      return;
    }

    const delta = s.nextMilestoneMin - s.minutes;
    const deltaSec = Math.max(0, delta * 60);
    this.subEl.textContent = `Próximo evento en ${deltaSec.toFixed(0)}s (${s.firedCount}/${s.totalMilestones})`;

    // Ventana fija de 2min para escalar el fill: a 2min del milestone = 0%,
    // al momento del milestone = 100%. Feedback visual de presión temporal.
    const warnWindow = 2;
    const consumed = 1 - Math.min(1, delta / warnWindow);
    this.fillEl.style.width = `${(consumed * 100).toFixed(0)}%`;

    this.container.classList.remove('warn', 'danger');
    if (delta < 0.5) {
      this.container.classList.add('danger');
      this.fillEl.style.background = '#FF453A';
    } else if (delta < 1.5) {
      this.container.classList.add('warn');
      this.fillEl.style.background = '#FF9F0A';
    } else {
      this.fillEl.style.background = '#30D158';
    }
  }

  private injectStyles(): void {
    if (document.getElementById('cgt-clock-styles')) return;
    const style = document.createElement('style');
    style.id = 'cgt-clock-styles';
    style.textContent = `
      #cgt-clock {
        position: fixed; top: 20px; left: 50%; transform: translateX(-50%);
        z-index: 300; min-width: 220px;
        padding: 12px 18px; border-radius: 14px;
        background: rgba(0,0,0,0.82); backdrop-filter: blur(18px);
        border: 0.5px solid rgba(255,255,255,0.08);
        color: #f5f5f7; font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        text-align: center;
        display: none;
        transition: border-color 0.3s, box-shadow 0.3s;
      }
      #cgt-clock.warn { border-color: rgba(255,159,10,0.45); box-shadow: 0 0 24px rgba(255,159,10,0.18); }
      #cgt-clock.danger { border-color: rgba(255,69,58,0.55); box-shadow: 0 0 28px rgba(255,69,58,0.25); animation: cgtClockPulse 1.2s ease-in-out infinite; }
      .cgt-clock-label { font-size: 9px; letter-spacing: 2px; color: #6e6e73; font-weight: 600; }
      .cgt-clock-time {
        font-family: 'JetBrains Mono', monospace;
        font-size: 26px; font-weight: 700; letter-spacing: -0.5px; margin: 2px 0;
      }
      .cgt-clock-sub { font-size: 11px; color: #a1a1a6; margin-bottom: 8px; }
      .cgt-clock-track {
        height: 3px; background: rgba(255,255,255,0.08);
        border-radius: 2px; overflow: hidden;
      }
      .cgt-clock-fill {
        height: 100%; background: #30D158; width: 0%;
        transition: width 0.4s ease, background 0.3s;
      }
      @keyframes cgtClockPulse {
        0%,100% { box-shadow: 0 0 28px rgba(255,69,58,0.25); }
        50%     { box-shadow: 0 0 36px rgba(255,69,58,0.45); }
      }
    `;
    document.head.appendChild(style);
  }
}

export default ClockHUD;
